import { InfoCard, Link, Progress } from '@backstage/core-components';
import { configApiRef, discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { Chip, List, ListItem, ListItemText, Theme, Typography, makeStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { ReactNode, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  ANNOUNCEMENTS_PATH,
  HomeAnnouncement,
  announcementCategoryPath,
  announcementViewPath,
  fetchAnnouncements,
  toIncident,
} from './homeAnnouncements';
import { readStatusConfig } from './homeConfig';
import { formatRelative, useAsyncValue } from './homeUtils';

const NEW_FOR_DAYS = 7;

const useStyles = makeStyles((theme: Theme) => ({
  list: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  item: {
    alignItems: 'flex-start',
    paddingLeft: 0,
    paddingRight: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  chip: {
    height: 22,
    fontSize: '0.72rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  outage: {
    color: theme.palette.error.contrastText,
    backgroundColor: theme.palette.error.main,
  },
  maintenance: {
    color: theme.palette.warning.contrastText,
    backgroundColor: theme.palette.warning.main,
  },
  category: {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
  },
  newChip: {
    color: theme.palette.success.main,
    border: `1px solid ${alpha(theme.palette.success.main, 0.5)}`,
    backgroundColor: 'transparent',
  },
  title: {
    fontWeight: 600,
  },
  excerpt: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
}));

export interface HomeAnnouncementsCardProps {
  title?: string;
  max?: number;
}

/**
 * Announcements card for the home page. Active Outage/Maintenance
 * announcements are pinned first; every item shows its category as a chip.
 */
export const HomeAnnouncementsCard = ({ title = 'Announcements', max = 5 }: HomeAnnouncementsCardProps) => {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const incidentConfig = useMemo(() => readStatusConfig(configApi).incidents, [configApi]);

  const { value, loading, error } = useAsyncValue(
    () => fetchAnnouncements(discoveryApi, fetchApi, { active: true, max: Math.max(max * 2, 10) }),
    [discoveryApi, fetchApi, max],
  );

  const announcements = useMemo(() => {
    const now = Date.now();
    const pinned = (a: HomeAnnouncement) => (toIncident(a, incidentConfig, now) ? 0 : 1);
    return [...(value ?? [])]
      .sort((a, b) => pinned(a) - pinned(b) || Date.parse(b.created_at) - Date.parse(a.created_at))
      .slice(0, max);
  }, [value, incidentConfig, max]);

  const categoryClass = (slug: string) => {
    if (slug === incidentConfig.outageCategory) return classes.outage;
    if (slug === incidentConfig.maintenanceCategory) return classes.maintenance;
    return classes.category;
  };

  const isNew = (announcement: HomeAnnouncement) =>
    Date.now() - Date.parse(announcement.created_at) <= NEW_FOR_DAYS * 24 * 3600 * 1000;

  let content: ReactNode;
  if (loading && !value) {
    content = <Progress />;
  } else if (error) {
    content = <Alert severity="info">Announcements are unavailable right now.</Alert>;
  } else if (announcements.length === 0) {
    content = (
      <Typography variant="body2" color="textSecondary">
        No announcements right now.
      </Typography>
    );
  } else {
    content = (
      <List dense className={classes.list}>
        {announcements.map(announcement => (
          <ListItem key={announcement.id} className={classes.item}>
            <ListItemText
              disableTypography
              primary={
                <>
                  {(announcement.category || isNew(announcement)) && (
                    <div className={classes.chips}>
                      {announcement.category && (
                        <Chip
                          size="small"
                          clickable
                          label={announcement.category.title}
                          className={`${classes.chip} ${categoryClass(announcement.category.slug)}`}
                          component={RouterLink}
                          to={announcementCategoryPath(announcement.category.slug)}
                        />
                      )}
                      {isNew(announcement) && (
                        <Chip size="small" label="New" className={`${classes.chip} ${classes.newChip}`} />
                      )}
                    </div>
                  )}
                  <Typography variant="body1" className={classes.title}>
                    <Link to={announcementViewPath(announcement.id)}>{announcement.title}</Link>
                  </Typography>
                </>
              }
              secondary={
                <Typography variant="body2" color="textSecondary" component="div">
                  <span>{formatRelative(announcement.created_at)}</span>
                  {announcement.excerpt && <div className={classes.excerpt}>{announcement.excerpt}</div>}
                </Typography>
              }
            />
          </ListItem>
        ))}
      </List>
    );
  }

  return (
    <InfoCard title={title} variant="gridItem" deepLink={{ link: ANNOUNCEMENTS_PATH, title: 'See all' }}>
      {content}
    </InfoCard>
  );
};
